import { getCryptoDecimals, isCryptoCode } from 'shared/src/cryptoUtils';
import { getCountryCode } from 'shared/src/getCountryCode';

import { virtualAccountNumberFormatter } from 'utils/formatters/virtualAccountNumberFormatter';
import { virtualAccountLabel } from 'utils/helpers';

import { AccountStatus } from 'api';

import type { VirtualAccount } from 'models/virtualAccount';

const ACCOUNT_STATUS_LABELS = {
  [AccountStatus.suspended]: 'Suspended',
  [AccountStatus.terminated]: 'Terminated'
} as const;

export type AccountWidgetViewState = {
  accountLabel: string;
  accountNumberLabel: string;
  balanceValue: string;
  countryCode: string;
  currencySymbol: string | null;
  isSharedAccount: boolean;
  isSuspendedAccount: boolean;
  isTerminatedAccount: boolean;
  isFundingUpdated: boolean | undefined;
  statusBadgeLabel?: (typeof ACCOUNT_STATUS_LABELS)[keyof typeof ACCOUNT_STATUS_LABELS];
};

const DEFAULT_FRACTION_DIGITS = 2;

type FormattedBalance = {
  currencySymbol: string | null;
  balanceValue: string;
};

const getCountryCodeSafe = (currencyCode: VirtualAccount['currency_code']) => {
  try {
    return getCountryCode(currencyCode);
  } catch {
    // eslint-disable-next-line
    console.warn(
      '<AccountWidget /> Failure getting country code for currency ',
      currencyCode
    );

    return '';
  }
};

const formatDecimalBalance = (
  currencyCode: string,
  numericBalance: number
): FormattedBalance => ({
  currencySymbol: null,
  balanceValue: new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: DEFAULT_FRACTION_DIGITS,
    maximumFractionDigits: getCryptoDecimals(currencyCode)
  }).format(numericBalance)
});

const formatFiatBalance = (
  currencyCode: string,
  numericBalance: number
): FormattedBalance => {
  const parts = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currencyCode || 'GBP',
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: DEFAULT_FRACTION_DIGITS,
    maximumFractionDigits: DEFAULT_FRACTION_DIGITS
  }).formatToParts(numericBalance);

  return {
    currencySymbol: parts.find((p) => p.type === 'currency')?.value ?? null,
    balanceValue: parts
      .filter((p) => p.type !== 'currency')
      .map((p) => p.value)
      .join('')
      .trim()
  };
};

const formatBalance = (
  currencyCode: string,
  numericBalance: number
): FormattedBalance => {
  if (isCryptoCode(currencyCode)) {
    return formatDecimalBalance(currencyCode, numericBalance);
  }

  try {
    return formatFiatBalance(currencyCode, numericBalance);
  } catch {
    return formatDecimalBalance(currencyCode, numericBalance);
  }
};

export const getAccountWidgetViewState = (
  accountInfo: VirtualAccount,
  isFundingUpdated: boolean | undefined
): AccountWidgetViewState => {
  const currencyCode = accountInfo.currency_code ?? '';
  const numericBalance = Number(accountInfo.balance);

  const { currencySymbol, balanceValue } = formatBalance(
    currencyCode,
    numericBalance
  );

  const isSharedAccount = !accountInfo.owner;
  const isSuspendedAccount = accountInfo.status === AccountStatus.suspended;
  const isTerminatedAccount = accountInfo.status === AccountStatus.terminated;
  const accountNumberLabel =
    virtualAccountNumberFormatter(accountInfo.account_number) ??
    accountInfo.account_number;

  return {
    accountLabel: virtualAccountLabel(accountInfo),
    accountNumberLabel,
    balanceValue: balanceValue || accountInfo.balance,
    countryCode: getCountryCodeSafe(accountInfo.currency_code),
    currencySymbol,
    isSharedAccount,
    isSuspendedAccount,
    isTerminatedAccount,
    isFundingUpdated,
    statusBadgeLabel: accountInfo.status
      ? ACCOUNT_STATUS_LABELS[
          accountInfo.status as keyof typeof ACCOUNT_STATUS_LABELS
        ]
      : undefined
  };
};
